import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Sparkles } from "lucide-react";
import { StatusChip, StatusStepper } from "./components";
import { API, shortDate, shortDateTime } from "./shared";

export default function ProjectDetailPage() {
  const { briefs } = useOutletContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const brief = briefs.find((b) => b.brief_id === id);

  if (!brief) {
    return (
      <div data-testid="project-not-found" className="mx-auto max-w-md text-center">
        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-amber-300">Project not found</p>
        <p className="mt-2 text-sm text-slate-400">This project is no longer in your library.</p>
        <Link
          to="/dashboard/projects"
          data-testid="project-not-found-back"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-[#1A1204] transition-colors hover:bg-amber-300"
        >
          Back to My Projects
        </Link>
      </div>
    );
  }

  const chips = [
    brief.niche && `Niche: ${brief.niche}`,
    brief.theme && `Theme: ${brief.theme}`,
    brief.style && `Style: ${brief.style}`,
  ].filter(Boolean);

  const revisions = [
    { label: "Create another variation", pre: `Create another variation of this design — keep the same audience and style, make it fully original. Original brief: "${brief.request}"` },
    { label: "Try another phrase", pre: `Keep this concept but create a new, more playful phrase. Original brief: "${brief.request}"` },
    { label: "Turn this into a collection", pre: `Create 10 original designs using this creative direction: "${brief.request}". Each design a distinct original concept for the same audience.`, count: 10 },
  ];

  return (
    <div data-testid="project-detail-page">
      <Link
        to="/dashboard/projects"
        data-testid="project-detail-back"
        className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to My Projects
      </Link>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="max-w-2xl text-xl font-extrabold tracking-tight text-white sm:text-2xl">{brief.request}</h1>
        <StatusChip status={brief.status} />
      </div>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">
        Submitted {shortDateTime(brief.created_at)}
      </p>

      <div className="glass mt-6 rounded-2xl p-5">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">Production progress</p>
        <div className="mt-4">
          <StatusStepper status={brief.status} />
        </div>
        {brief.status === "Submitted" && brief.queue_position > 0 && (
          <p data-testid="project-queue-position" className="mt-3 text-xs font-medium text-sky-300">
            Queue position #{brief.queue_position}
          </p>
        )}
      </div>

      <div className="glass mt-4 rounded-2xl p-5">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-sky-400">Original brief</p>
        <p className="mt-3 text-sm leading-relaxed text-slate-200">{brief.request}</p>
        {chips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {chips.map((c) => (
              <span key={c} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300">
                {c}
              </span>
            ))}
          </div>
        )}
        {brief.ref_path && (
          <div className="mt-4">
            <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Reference image</p>
            <img
              data-testid="project-reference-image"
              src={`${API}/files/${brief.ref_path}?inline=1`}
              alt="Reference"
              className="mt-2 h-28 w-28 rounded-xl border border-white/10 bg-[#070B14] object-cover"
            />
          </div>
        )}
      </div>

      {brief.status === "Delivered" && brief.delivered_path && (
        <div className="mt-4 rounded-2xl border border-emerald-400/25 bg-emerald-400/5 p-5" data-testid="project-final-files">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-300">
            Final files — delivered {shortDate(brief.delivered_at)}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <img
              src={`${API}/files/${brief.delivered_path}`}
              alt="Delivered design preview"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="h-20 w-20 rounded-xl border border-white/10 bg-[#070B14] object-cover"
            />
            <div>
              <a
                data-testid="project-download-button"
                href={`${API}/files/${brief.delivered_path}`}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-5 py-2.5 text-xs font-extrabold uppercase tracking-wide text-[#05140d] transition-colors hover:bg-emerald-300"
              >
                <FileDown className="h-3.5 w-3.5" />
                Download PNG
              </a>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                PNG · Transparent Background · High Resolution
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="glass mt-4 rounded-2xl p-5" data-testid="project-revision-block">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
          Want a revision or a variation?
        </p>
        <p className="mt-1.5 text-xs text-slate-400">
          Submit a new brief carrying this creative direction — the studio keeps your context.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {revisions.map((a) => (
            <button
              key={a.label}
              data-testid={`project-revision-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() =>
                navigate("/dashboard/new", {
                  state: { prefill: a.pre, count: a.count || 1, style: brief.style || "", niche: brief.niche || "", theme: brief.theme || "" },
                })
              }
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-slate-300 transition-colors hover:border-amber-400/40 hover:text-amber-300"
            >
              <Sparkles className="h-3 w-3" />
              {a.label}
              {a.count > 1 ? ` · ${a.count} credits` : ""}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
